import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  Plus, Edit2, Trash2, Copy, Star, ArrowLeft, FileText, CheckCircle2, ExternalLink,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * InvoiceTemplatesManager — admin list page for invoice templates.
 *
 * Mirrors the FormFieldsManager visual pattern: header with back-arrow,
 * "Add new" CTA, table of items, inline actions (set default / clone /
 * edit / delete). Editing drills into InvoiceTemplateEditor at
 * /invoice-templates/:id.
 *
 * The default template cannot be deleted — backend enforces this; we also
 * disable the button in the UI for clarity.
 */
const InvoiceTemplatesManager = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/invoice-templates`, authHeader());
      setTemplates(res.data || []);
    } catch (e) {
      toast.error('Failed to load invoice templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    const name = window.prompt('Name for the new template:', 'New invoice template');
    if (!name || !name.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(
        `${API_URL}/invoice-templates`,
        { name: name.trim() },
        authHeader()
      );
      toast.success('Template created');
      navigate(`/invoice-templates/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to create template');
    } finally {
      setCreating(false);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await axios.post(`${API_URL}/invoice-templates/${id}/set-default`, {}, authHeader());
      toast.success('Default template updated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to set default');
    }
  };

  const handleClone = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/invoice-templates/${id}/clone`, {}, authHeader());
      toast.success('Template duplicated');
      navigate(`/invoice-templates/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to clone template');
    }
  };

  const handleDelete = async (tpl) => {
    if (tpl.is_default) {
      toast.warning('You cannot delete the default template. Set another template as default first.');
      return;
    }
    if (!window.confirm(`Delete "${tpl.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/invoice-templates/${tpl.id}`, authHeader());
      toast.success('Template deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to delete template');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin?tab=settings')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Settings"
            >
              <ArrowLeft size={24} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
                <FileText size={28} className="text-primary-600" />
                <span>Invoice Templates</span>
              </h1>
              <p className="text-gray-600 mt-1">
                Customize invoice and receipt PDFs. Create insurance-ready templates,
                add custom client questions, and configure what appears on each layout.
              </p>
            </div>
          </div>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="btn btn-primary flex items-center space-x-2 disabled:opacity-50"
          >
            <Plus size={20} />
            <span>{creating ? 'Creating…' : 'New template'}</span>
          </button>
        </div>

        {templates.length === 0 ? (
          <div className="card text-center py-12">
            <FileText size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No templates yet</h3>
            <p className="text-gray-500 mb-4">
              Create your first template to start generating PDF invoices.
            </p>
            <button
              onClick={handleCreate}
              className="btn btn-primary inline-flex items-center space-x-2"
            >
              <Plus size={18} />
              <span>Create your first template</span>
            </button>
          </div>
        ) : (
          <div className="card p-0 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Template</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Last updated</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {templates.map((tpl) => (
                  <tr key={tpl.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <FileText size={20} className="text-primary-600" />
                        <div>
                          <div className="font-semibold text-gray-900">{tpl.name}</div>
                          <div className="text-xs text-gray-500">ID: {String(tpl.id).slice(0, 8)}…</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {tpl.is_default ? (
                        <span className="inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                          <CheckCircle2 size={14} />
                          <span>Default</span>
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSetDefault(tpl.id)}
                          className="text-xs text-primary-600 hover:underline inline-flex items-center space-x-1"
                          title="Make this the default template for new invoices"
                        >
                          <Star size={14} />
                          <span>Set as default</span>
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {tpl.updated_at ? String(tpl.updated_at).slice(0, 10) : '—'}
                    </td>
                    <td className="px-6 py-4 text-right space-x-2 whitespace-nowrap">
                      <a
                        href={`${API_URL}/invoice-templates/${tpl.id}/preview`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-1 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                        title="Open a preview in a new tab"
                        onClick={(e) => {
                          // The preview endpoint is auth-gated; we can't pass
                          // a Bearer header on a plain link. Open via fetch +
                          // blob so the auth header is included.
                          e.preventDefault();
                          openPreview(tpl.id);
                        }}
                      >
                        <ExternalLink size={14} />
                        <span>Preview</span>
                      </a>
                      <button
                        onClick={() => navigate(`/invoice-templates/${tpl.id}`)}
                        className="inline-flex items-center space-x-1 px-3 py-1 text-sm bg-primary-50 hover:bg-primary-100 text-primary-700 rounded transition-colors"
                      >
                        <Edit2 size={14} />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => handleClone(tpl.id)}
                        className="inline-flex items-center space-x-1 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                      >
                        <Copy size={14} />
                        <span>Clone</span>
                      </button>
                      <button
                        onClick={() => handleDelete(tpl)}
                        disabled={tpl.is_default}
                        className="inline-flex items-center space-x-1 px-3 py-1 text-sm bg-red-50 hover:bg-red-100 text-red-700 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title={tpl.is_default ? 'The default template cannot be deleted' : 'Delete'}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <strong className="text-blue-900">Quick tip:</strong>{' '}
          Use multiple templates to support different scenarios — e.g., a
          plain receipt for retail customers, an "Insurance-ready" template
          with provider license number and service codes for wellness
          clients, and a "Quote" variant with no payment fields. Mark one as
          default; the others can be selected manually per appointment.
        </div>
      </div>
    </div>
  );
};

/**
 * Open the preview HTML in a new tab. Because the endpoint is admin-auth
 * gated we fetch with the Bearer token, then open the response as a Blob
 * URL.
 */
async function openPreview(id) {
  try {
    const res = await fetch(`${API_URL}/invoice-templates/${id}/preview`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
    });
    if (!res.ok) throw new Error('Preview failed');
    const html = await res.text();
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error('Could not load preview');
  }
}

export default InvoiceTemplatesManager;
